import { TabType } from '@heidi/session/contracts';
import { type Tab } from '@heidi/session/data';

/**
 * The one session surface a focused pane shows — its note, a sub-document,
 * Context, or Transcript. Kept in its own dependency-free module so the light Work
 * shell (which decides the target and pins it on "Open in session") and the
 * lazy V2 panel chunk share the type and the `Tab` mapping at runtime without
 * the shell gaining an edge into the chunk.
 */
export type FocusedArtifactTarget =
  | { kind: 'context' }
  | { kind: 'transcript' }
  | { kind: 'note' }
  | { kind: 'document'; documentId: string };

export const focusedArtifactTab = (target: FocusedArtifactTarget): Tab => {
  switch (target.kind) {
    case 'context':
      return { name: TabType.CONTEXT };
    case 'transcript':
      return { name: TabType.TRANSCRIPT };
    case 'note':
      return { name: TabType.NOTE };
    case 'document':
      return { name: TabType.DOCUMENT, documentId: target.documentId };
  }
};

/**
 * Maps the agent-emitted focus (`focusTab` + `focusDocumentId`) to a focused
 * target; `null` for anything the focused pane does not render (no focus or a
 * document tab without an id), which keeps the full session panel.
 */
export const toFocusedArtifactTarget = (
  focusTab: TabType | undefined,
  focusDocumentId: string | undefined
): FocusedArtifactTarget | null => {
  if (focusTab === TabType.DOCUMENT && focusDocumentId) {
    return { kind: 'document', documentId: focusDocumentId };
  }
  if (focusTab === TabType.CONTEXT) return { kind: 'context' };
  if (focusTab === TabType.TRANSCRIPT) return { kind: 'transcript' };
  return focusTab === TabType.NOTE ? { kind: 'note' } : null;
};
